/**
 * ConcentrationPage — household concentration risk dashboard
 *
 * Surfaces single-name, sector, currency, region and asset-class
 * concentrations across the entire household so idiosyncratic risk
 * can't hide inside an account aggregate.
 *
 * The platform previously buried MSFT at ~60% of the AJ Bell SIPP XO
 * (the single biggest undiversified name in the household). This page
 * makes risks of that shape the first thing on the screen.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  RefreshCw,
  ShieldAlert,
  Coins,
  Globe2,
  PieChart,
  Building2,
  Briefcase,
  DoorOpen,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

type Severity = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL'

interface SingleStockHousehold {
  name: string
  symbol: string | null
  asset_type: string | null
  sector: string | null
  region: string | null
  accounts: string[]
  value_gbp: number
  pct_of_household: number
  severity: Severity
}

interface SingleStockInAccount {
  account_id: number
  account_name: string
  owner: string
  discretionary: boolean
  account_total_gbp: number
  holding_name: string
  holding_symbol: string | null
  holding_value_gbp: number
  pct_of_account: number
  pct_of_household: number
  severity: Severity
}

interface BucketItem {
  key: string
  value_gbp: number
  pct_of_household: number
  severity: Severity
}

interface ConcentrationRisks {
  household_total_gbp: number
  single_stock_household: SingleStockHousehold[]
  single_stock_in_account: SingleStockInAccount[]
  sector_concentration: BucketItem[]
  currency_concentration: BucketItem[]
  region_concentration: BucketItem[]
  asset_class_concentration: BucketItem[]
  crypto_pct: number
  summary: string[]
  thresholds: {
    single_stock_household: { moderate: number; high: number; critical: number }
    single_stock_in_account: { high: number; critical: number }
    bucket: { moderate: number; high: number; critical: number }
  }
}

type ExecBand = 'severe' | 'high' | 'moderate' | 'low'

interface ExecutionRiskPosition {
  name: string
  symbol: string | null
  asset_type: string | null
  account_id: number | null
  value_gbp: number
  weight_pct: number
  illiquidity: number
  execution_risk_score: number
  band: ExecBand
  note: string
}

interface ExecutionRisk {
  book_total_gbp: number
  positions: ExecutionRiskPosition[]
  illiquid_pct: number
  worst_band: ExecBand
  top_exit_risks: {
    name: string
    band: ExecBand
    weight_pct: number
    execution_risk_score: number
    note: string
  }[]
  summary: string[]
  methodology: string
}

type TabKey = 'stocks' | 'sector' | 'currency' | 'region' | 'asset_class' | 'execution'

const fmt = (n: number) =>
  `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

const fmtPct = (n: number) => `${n.toFixed(1)}%`

const SEVERITY_BADGE: Record<Severity, string> = {
  LOW: 'badge-green',
  MODERATE: 'badge-amber',
  HIGH: 'badge-amber',
  CRITICAL: 'badge-red',
}

const SEVERITY_BAR: Record<Severity, string> = {
  LOW: 'bg-emerald/50',
  MODERATE: 'bg-amber/60',
  HIGH: 'bg-amber',
  CRITICAL: 'bg-rose',
}

// Execution / liquidity-risk band styling: severe rose / high amber /
// moderate zinc / low muted.
const EXEC_BAND_BADGE: Record<ExecBand, string> = {
  severe: 'badge-red',
  high: 'badge-amber',
  moderate: 'bg-surface-hover text-text-secondary border border-border',
  low: 'bg-surface-card text-text-muted border border-border',
}

const EXEC_BAND_BAR: Record<ExecBand, string> = {
  severe: 'bg-rose',
  high: 'bg-amber',
  moderate: 'bg-text-muted/60',
  low: 'bg-text-muted/40',
}

function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span className={clsx('badge text-[10px]', SEVERITY_BADGE[severity])}>
      {severity}
    </span>
  )
}

function BucketTable({ items, unit }: { items: BucketItem[]; unit: string }) {
  if (items.length === 0) {
    return <div className="card p-4 text-sm text-text-muted">No data.</div>
  }
  const max = Math.max(...items.map(i => i.pct_of_household), 1)
  return (
    <div className="card overflow-hidden">
      <div className="table-responsive">
        <table className="table-pro w-full text-xs">
          <thead className="bg-surface-card border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-text-secondary">
                {unit}
              </th>
              <th className="!text-right px-2 py-3 font-semibold text-text-secondary">
                Value
              </th>
              <th className="!text-right px-2 py-3 font-semibold text-text-secondary">
                % of Household
              </th>
              <th className="text-left px-2 py-3 font-semibold text-text-secondary w-40">
                Distribution
              </th>
              <th className="!text-right px-4 py-3 font-semibold text-text-secondary">
                Severity
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.key} className="border-b border-border hover:bg-surface-hover/50">
                <td className="px-4 py-3 text-text-primary font-medium">{item.key}</td>
                <td className="text-right px-2 py-3 font-mono text-text-primary">
                  {fmt(item.value_gbp)}
                </td>
                <td className="text-right px-2 py-3 font-mono text-text-secondary">
                  {fmtPct(item.pct_of_household)}
                </td>
                <td className="px-2 py-3">
                  <div className="h-2 bg-surface-card rounded overflow-hidden">
                    <div
                      className={clsx('h-full', SEVERITY_BAR[item.severity])}
                      style={{ width: `${(item.pct_of_household / max) * 100}%` }}
                    />
                  </div>
                </td>
                <td className="text-right px-4 py-3">
                  <SeverityBadge severity={item.severity} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function ConcentrationPage() {
  const [data, setData] = useState<ConcentrationRisks | null>(null)
  const [exec, setExec] = useState<ExecutionRisk | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<TabKey>('stocks')

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<ConcentrationRisks>('/api/concentration/risks')
      setData(res.data)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load concentration risks')
    } finally {
      setLoading(false)
    }
    // Execution / liquidity risk is additive — never block the page on it.
    try {
      const er = await api.get<ExecutionRisk>('/api/concentration/execution-risk')
      setExec(er.data)
    } catch {
      setExec(null)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const criticalAccountRisks = useMemo(
    () =>
      (data?.single_stock_in_account ?? []).filter(
        i => i.severity === 'HIGH' || i.severity === 'CRITICAL',
      ),
    [data],
  )

  if (loading && !data) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-8 w-64 skeleton" />
        <div className="h-32 skeleton" />
        <div className="h-64 skeleton" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error ?? 'No data'}</p>
        <button type="button" onClick={fetchData} className="btn-secondary mt-3">
          Retry
        </button>
      </div>
    )
  }

  const worstSeverityInSummary: Severity = data.summary.some(s => s.includes('CRITICAL'))
    ? 'CRITICAL'
    : data.summary.some(s => s.includes('HIGH'))
      ? 'HIGH'
      : 'MODERATE'

  const bannerTone =
    worstSeverityInSummary === 'CRITICAL'
      ? 'border-rose/40 bg-rose/5'
      : 'border-amber/30 bg-amber/5'

  const TABS: { key: TabKey; label: string; icon: JSX.Element }[] = [
    { key: 'stocks', label: 'Single Stock', icon: <Briefcase size={13} aria-hidden /> },
    { key: 'sector', label: 'Sector', icon: <PieChart size={13} aria-hidden /> },
    { key: 'currency', label: 'Currency', icon: <Coins size={13} aria-hidden /> },
    { key: 'region', label: 'Region', icon: <Globe2 size={13} aria-hidden /> },
    { key: 'asset_class', label: 'Asset Class', icon: <Building2 size={13} aria-hidden /> },
    ...(exec
      ? [{ key: 'execution' as TabKey, label: 'Exit Risk', icon: <DoorOpen size={13} aria-hidden /> }]
      : []),
  ]

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ShieldAlert size={20} className="text-rose-light" aria-hidden />
            Concentration Risk
          </h1>
          <p className="text-sm text-text-secondary">
            Where your household is over-exposed — single names, sectors, currencies, regions, asset classes
          </p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary gap-2 text-xs">
          <RefreshCw size={13} aria-hidden />
          Refresh
        </button>
      </div>

      {/* Critical risks banner */}
      <div className={clsx('card p-4 border-2', bannerTone)}>
        <div className="flex items-start gap-3">
          <AlertTriangle
            size={18}
            className={clsx(
              'shrink-0 mt-0.5',
              worstSeverityInSummary === 'CRITICAL' ? 'text-rose-light' : 'text-amber',
            )}
            aria-hidden
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-text-primary">
              Top {data.summary.length} concentration risk{data.summary.length === 1 ? '' : 's'}
            </p>
            <ul className="mt-2 space-y-1.5">
              {data.summary.map((s, i) => (
                <li key={i} className="text-xs text-text-secondary leading-relaxed">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
        <div className="stat-card">
          <p className="stat-label">Household Total</p>
          <p className="stat-value">{fmt(data.household_total_gbp)}</p>
          <p className="text-xs text-text-muted">tracked assets</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Top Single Name</p>
          <p className="stat-value text-amber-light">
            {data.single_stock_household[0]
              ? fmtPct(data.single_stock_household[0].pct_of_household)
              : '—'}
          </p>
          <p className="text-xs text-text-muted truncate">
            {data.single_stock_household[0]?.name ?? '—'}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Crypto</p>
          <p
            className={clsx(
              'stat-value',
              data.crypto_pct > 10 ? 'text-rose-light' : 'text-text-primary',
            )}
          >
            {fmtPct(data.crypto_pct)}
          </p>
          <p className="text-xs text-text-muted">of household</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">HIGH/CRITICAL Accounts</p>
          <p
            className={clsx(
              'stat-value',
              criticalAccountRisks.length > 0 ? 'text-rose-light' : 'text-emerald-light',
            )}
          >
            {criticalAccountRisks.length}
          </p>
          <p className="text-xs text-text-muted">over-concentrated accounts</p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-1 glass rounded-lg p-1 flex-wrap">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded transition-colors flex items-center gap-1.5',
              tab === t.key
                ? 'bg-surface-hover text-text-primary'
                : 'text-text-muted hover:text-text-secondary',
            )}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'stocks' && (
        <div className="space-y-6">
          {/* Top 10 household single names */}
          <section>
            <h2 className="panel-title mb-3">
              Top 10 Single Names — % of Household
            </h2>
            <div className="card overflow-hidden">
              <div className="table-responsive">
                <table className="table-pro w-full text-xs">
                  <thead className="bg-surface-card border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-text-secondary">#</th>
                      <th className="text-left px-2 py-3 font-semibold text-text-secondary">Name</th>
                      <th className="text-left px-2 py-3 font-semibold text-text-secondary">Sector</th>
                      <th className="!text-right px-2 py-3 font-semibold text-text-secondary">Value</th>
                      <th className="!text-right px-2 py-3 font-semibold text-text-secondary">
                        % Household
                      </th>
                      <th className="!text-right px-4 py-3 font-semibold text-text-secondary">
                        Severity
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.single_stock_household.map((item, i) => (
                      <tr
                        key={`${item.name}-${i}`}
                        className="border-b border-border hover:bg-surface-hover/50"
                      >
                        <td className="px-4 py-3 font-mono text-text-muted">{i + 1}</td>
                        <td className="px-2 py-3">
                          <div className="text-text-primary font-medium">{item.name}</div>
                          {item.symbol && (
                            <div className="text-[10px] text-text-muted font-mono">
                              {item.symbol}
                            </div>
                          )}
                          {item.accounts.length > 1 && (
                            <div className="text-[10px] text-text-muted mt-0.5">
                              across {item.accounts.length} accounts
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-3 text-text-secondary">{item.sector ?? '—'}</td>
                        <td className="text-right px-2 py-3 font-mono font-semibold text-text-primary">
                          {fmt(item.value_gbp)}
                        </td>
                        <td className="text-right px-2 py-3 font-mono text-text-secondary">
                          {fmtPct(item.pct_of_household)}
                        </td>
                        <td className="text-right px-4 py-3">
                          <SeverityBadge severity={item.severity} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-[10px] text-text-muted mt-2">
              Thresholds: LOW &lt;{data.thresholds.single_stock_household.moderate}% ·
              MODERATE {data.thresholds.single_stock_household.moderate}-
              {data.thresholds.single_stock_household.high}% ·
              HIGH {data.thresholds.single_stock_household.high}-
              {data.thresholds.single_stock_household.critical}% · CRITICAL &gt;
              {data.thresholds.single_stock_household.critical}%.
            </p>
          </section>

          {/* Per-account critical concentrations */}
          <section>
            <h2 className="panel-title mb-3">
              Over-Concentrated Accounts — Largest Position ≥25% of Account
            </h2>
            {criticalAccountRisks.length === 0 ? (
              <div className="card p-4 text-sm text-text-muted">
                No accounts are over-concentrated in a single name. All tracked accounts are
                reasonably diversified.
              </div>
            ) : (
              <div className="space-y-3">
                {criticalAccountRisks.map(item => (
                  <div
                    key={item.account_id}
                    className={clsx(
                      'card p-4',
                      item.severity === 'CRITICAL' ? 'border-rose/30' : 'border-amber/20',
                    )}
                  >
                    <div className="flex items-start gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-text-primary">
                            {item.holding_name}
                          </p>
                          {item.holding_symbol && (
                            <span className="text-[10px] font-mono text-text-muted">
                              {item.holding_symbol}
                            </span>
                          )}
                          <SeverityBadge severity={item.severity} />
                        </div>
                        <p className="text-xs text-text-secondary mt-1">
                          {fmt(item.holding_value_gbp)} — {fmtPct(item.pct_of_account)} of{' '}
                          <span className="text-text-primary">{item.account_name}</span>{' '}
                          <span className="text-text-muted">· {item.owner}</span>
                          {item.discretionary ? (
                            <span className="badge badge-green text-[10px] ml-2">
                              DISCRETIONARY
                            </span>
                          ) : (
                            <span className="badge-neutral text-[10px] ml-2">
                              MANAGED
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-text-muted mt-1">
                          {fmtPct(item.pct_of_household)} of household · account total{' '}
                          {fmt(item.account_total_gbp)}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p
                          className={clsx(
                            'text-2xl font-bold',
                            item.severity === 'CRITICAL' ? 'text-rose-light' : 'text-amber-light',
                          )}
                        >
                          {fmtPct(item.pct_of_account)}
                        </p>
                        <p className="text-[10px] text-text-muted">of account</p>
                      </div>
                    </div>
                    <div className="mt-3 h-2 bg-surface-card rounded overflow-hidden">
                      <div
                        className={clsx('h-full', SEVERITY_BAR[item.severity])}
                        style={{ width: `${Math.min(item.pct_of_account, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-text-muted mt-2">
              Thresholds: HIGH &gt;{data.thresholds.single_stock_in_account.high}% of account ·
              CRITICAL &gt;{data.thresholds.single_stock_in_account.critical}% of account.
              Cash-only and property accounts are excluded.
            </p>
          </section>
        </div>
      )}

      {tab === 'sector' && (
        <section>
          <h2 className="panel-title mb-3">Sector Concentration</h2>
          <BucketTable items={data.sector_concentration} unit="Sector" />
        </section>
      )}

      {tab === 'currency' && (
        <section>
          <h2 className="panel-title mb-3">Currency Concentration</h2>
          <BucketTable items={data.currency_concentration} unit="Currency" />
        </section>
      )}

      {tab === 'region' && (
        <section>
          <h2 className="panel-title mb-3">Region Concentration</h2>
          <BucketTable items={data.region_concentration} unit="Region" />
        </section>
      )}

      {tab === 'asset_class' && (
        <section>
          <h2 className="panel-title mb-3">
            Asset Class Concentration
          </h2>
          <BucketTable items={data.asset_class_concentration} unit="Asset Class" />
        </section>
      )}

      {tab === 'execution' && exec && (
        <section className="space-y-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <h2 className="panel-title flex items-center gap-2">
                <DoorOpen size={15} className="text-amber" aria-hidden />
                Execution / Liquidity Risk
              </h2>
              <p className="text-xs text-text-secondary mt-1">
                Which positions are hard to <span className="font-semibold">exit</span> cleanly —
                large <span className="font-semibold">and</span> illiquid. "Optimal on paper,
                impossible in practice."
              </p>
            </div>
          </div>

          {/* Headline */}
          <div
            className={clsx(
              'card p-4 border-2',
              exec.worst_band === 'severe'
                ? 'border-rose/40 bg-rose/5'
                : exec.worst_band === 'high'
                  ? 'border-amber/30 bg-amber/5'
                  : 'border-border',
            )}
          >
            <p className="text-sm font-semibold text-text-primary">
              <span
                className={clsx(
                  (exec.illiquid_pct ?? 0) > 20 ? 'text-rose-light' : 'text-amber-light',
                )}
              >
                {fmtPct(exec.illiquid_pct ?? 0)}
              </span>{' '}
              of your investable book is in hard-to-exit assets
            </p>
            {(exec.summary ?? []).length > 0 && (
              <ul className="mt-2 space-y-1.5">
                {(exec.summary ?? []).slice(1).map((s, i) => (
                  <li key={i} className="text-xs text-text-secondary leading-relaxed">
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Top exit-risk positions */}
          {(exec.positions ?? []).length === 0 ? (
            <div className="card p-4 text-sm text-text-muted">
              No positions to score for exit risk.
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="table-responsive">
                <table className="table-pro w-full text-xs">
                  <thead className="bg-surface-card border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-text-secondary">Position</th>
                      <th className="!text-right px-2 py-3 font-semibold text-text-secondary">% Book</th>
                      <th className="text-left px-2 py-3 font-semibold text-text-secondary w-32">
                        Exit Risk
                      </th>
                      <th className="!text-right px-2 py-3 font-semibold text-text-secondary">Score</th>
                      <th className="!text-right px-4 py-3 font-semibold text-text-secondary">Band</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(exec.positions ?? []).map((p, i) => (
                      <tr
                        key={`${p.name}-${p.account_id ?? i}`}
                        className="border-b border-border hover:bg-surface-hover/50"
                      >
                        <td className="px-4 py-3">
                          <div className="text-text-primary font-medium">{p.name}</div>
                          <div className="text-[10px] text-text-muted">{p.note}</div>
                        </td>
                        <td className="text-right px-2 py-3 font-mono text-text-secondary">
                          {fmtPct(p.weight_pct ?? 0)}
                        </td>
                        <td className="px-2 py-3">
                          <div className="h-2 bg-surface-card rounded overflow-hidden">
                            <div
                              className={clsx('h-full', EXEC_BAND_BAR[p.band])}
                              style={{
                                width: `${Math.min(p.execution_risk_score ?? 0, 100)}%`,
                              }}
                            />
                          </div>
                        </td>
                        <td className="text-right px-2 py-3 font-mono text-text-primary">
                          {(p.execution_risk_score ?? 0).toFixed(0)}
                        </td>
                        <td className="text-right px-4 py-3">
                          <span className={clsx('badge text-[10px]', EXEC_BAND_BADGE[p.band])}>
                            {p.band}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="text-[10px] text-text-muted leading-relaxed">
            {exec.methodology}
          </p>
        </section>
      )}

      {/* Methodology */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold">
          Methodology &amp; thresholds
        </summary>
        <div className="mt-3 space-y-2">
          <p>
            <span className="text-text-muted">Single stock (household):</span> holdings grouped by
            name across all accounts. LOW &lt;2%, MODERATE 2-5%, HIGH 5-10%, CRITICAL &gt;10% of
            household.
          </p>
          <p>
            <span className="text-text-muted">Single stock (in account):</span> largest non-cash,
            non-property position inside each account. HIGH &gt;25% of account, CRITICAL &gt;50% of
            account. Only HIGH/CRITICAL accounts are shown.
          </p>
          <p>
            <span className="text-text-muted">Sector / currency / region / asset class:</span>{' '}
            MODERATE &gt;15%, HIGH &gt;30%, CRITICAL &gt;50% of household.
          </p>
          <p>
            <span className="text-text-muted">Crypto dust:</span> holdings under £1 excluded from
            individual rankings but still counted in total crypto exposure.
          </p>
        </div>
      </details>
    </div>
  )
}
